import React, { useRef, useState, useEffect, useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import cn from 'classnames';
import PropTypes from 'prop-types';
import useDidUpdateEffect from 'hooks/use-did-update-effect';
import stringHelper from 'js/string-helper';
import PagerLang from 'components/models/pager-lang/pager-lang';

const Pager = ({
  totalPages,
  page,
  pageRangeDisplayed = 2,
  className,
  lang = {
    prevLabel: 'Prev',
    pageLabel: '{0} of {1}',
    nextLabel: 'Next',
    jumpBackwardAriaLabel: 'Jump backward',
    jumpForwardAriaLabel: 'Jump forward',
  },
  onPageChange,
}) => {
  const pagerSelectRef = useRef(null);
  const pagerWrapperRef = useRef(null);
  const pagerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(page);

  useDidUpdateEffect(() => {
    if (currentPage !== page) {
      onPageChange(currentPage);
    }
  }, [currentPage]);

  useDidUpdateEffect(() => {
    setCurrentPage(page);
  }, [page]);

  const pagerSelectOptions = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  useEffect(() => {
    // This useEffect is required to achieve a custom mobile pagination design, where the mobile select element
    // is placed between the “Previous” and “Next” buttons, which are part of the react-paginate component.
    // That's why we need to cut it from the markup and place it manually before the first pager item.
    if (pagerWrapperRef?.current && pagerSelectRef?.current && totalPages) {
      const ul = pagerWrapperRef.current.querySelector('ul');
      if (!ul) return;

      const li = document.createElement('li');

      li.appendChild(pagerSelectRef.current);

      const firstPagerItem = ul.querySelector('li');
      if (firstPagerItem) {
        firstPagerItem.parentNode.insertBefore(li, firstPagerItem.nextSibling);
      }
    }
  }, [pagerSelectRef, pagerWrapperRef, totalPages]);

  const onMobileSelectorChange = e => {
    const newPage = +e.target.value;
    setCurrentPage(newPage);

    if (pagerRef && pagerRef.current) {
      pagerRef.current.handlePageChange(newPage - 1);
    }
  };

  const onPagerPageChange = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  return totalPages > 1 ? (
    <div
      className={cn('pager', {
        [className]: className,
      })}
    >
      {pagerSelectOptions && pagerSelectOptions.length > 0 && (
        <select
          ref={pagerSelectRef}
          className="pager__mobile-selector"
          value={currentPage}
          onChange={onMobileSelectorChange}
        >
          {pagerSelectOptions.map(option => (
            <option key={option} value={option}>
              {stringHelper.format(lang.pageLabel, option, totalPages)}
            </option>
          ))}
        </select>
      )}
      <div className="pager__wrapper" ref={pagerWrapperRef}>
        <ReactPaginate
          ref={pagerRef}
          pageCount={totalPages}
          forcePage={currentPage - 1}
          previousLabel={lang.prevLabel}
          nextLabel={lang.nextLabel}
          ariaLabelBuilder={pageIndex =>
            stringHelper.format(lang.pageLabel, pageIndex, totalPages)
          }
          breakAriaLabels={{
            forward: lang.jumpBackwardAriaLabel,
            backward: lang.jumpForwardAriaLabel,
          }}
          hrefAllControls={true}
          breakClassName="pager__break"
          disableInitialCallback={true}
          pageRangeDisplayed={pageRangeDisplayed}
          marginPagesDisplayed={1}
          containerClassName="pager__list"
          pageClassName="pager__item"
          pageLinkClassName="pager__link"
          previousClassName="pager__prev"
          nextClassName="pager__next"
          onPageChange={onPagerPageChange}
        />
      </div>
    </div>
  ) : null;
};

Pager.viewModelMeta = 'ignore';

Pager.propTypes = {
  className: PropTypes.string,
  pageRangeDisplayed: PropTypes.number,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  lang: PropTypes.shape(PagerLang.propTypes).isRequired,
  onPageChange: PropTypes.func,
};

export default Pager;
